// MyWallet.jsx
import React, { useCallback, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useFetch from '../../hooks/useFetch';
import Loader from '../../components/utils/Loader';
import Tooltip from '../../components/utils/Tooltip';
import MainLayout from '../../layouts/MainLayout';
import './MyWallet.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGift } from '@fortawesome/free-solid-svg-icons';

const MyWalletPoints = () => {
  const authState = useSelector(state => state.authReducer);
  const userId = authState.user._id
  const [fetchData, { loading }] = useFetch();
  const navigate = useNavigate();
  const [walletBalanceAmount, setWalletBalanceAmount] = useState(0.00);
  const [walletBalancePoints, setWalletBalancePoints] = useState(0);
  const [walletLastUpdated, setWalletLastUpdated] = useState();
  const [walletLastUpdatedRemarks, setWalletLastUpdatedRemarks] = useState('');

  const [walletData, setWalletData] = useState([]);
  const [selectedWalletId, setSelectedWalletId] = useState([]);

  //WalletBalance
  const fetchWalletBalance = useCallback(() => {
    const config = {
      url: "/walletbalance",
      method: "get",
      headers: { Authorization: authState.token }
    };

    fetchData(config, { showSuccessToast: false })
      .then(response => {
        // console.log("Response:", response);
        if (response) {
          // console.log("Response in array:", response);
          setWalletBalanceAmount(response.balanceAmount);
          setWalletBalancePoints(response.balancePoints);
          setWalletLastUpdated(response.lastTransactionDate);
          setWalletLastUpdatedRemarks(response.remarks);
        } else {
          console.error("Invalid response format: Wallet not found in response");
        }
      })
      .catch(error => {
        console.error("Error fetching wallet master:", error);
      });
  }, [authState.token, fetchData]);

  useEffect(() => {
    if (!authState.isLoggedIn) return;
    fetchWalletBalance();
  }, [authState.isLoggedIn, fetchWalletBalance]);

  //Wallet Transactions
  const fetchWallet = useCallback(() => {
    const config = {
      url: "/wallet",
      method: "get",
      headers: { Authorization: authState.token }
    };

    fetchData(config, { showSuccessToast: false })
      .then(response => {
        // console.log("Response:", response);
        if (response && Array.isArray(response)) {
          setWalletData(response);
        } else {
          console.error("Invalid response format: Wallet transaction data not found in response");
        }
      })
      .catch(error => {
        console.error("Error fetching wallets:", error);
      });
  }, [authState.token, fetchData]);

  useEffect(() => {
    if (!authState.isLoggedIn) return;
    fetchWallet();
  }, [authState.isLoggedIn, fetchWallet]);

  const sortedWalletData = [...walletData].sort((a, b) => new Date(b.dateTransaction) - new Date(a.dateTransaction));

  const handleWalletView = (_id) => {
    setSelectedWalletId(prevId => (prevId === _id ? null : _id));
  };

  const handleTopupClick = (userId) => {
    navigate('/wallet-topup', { state: { userId: userId } });
  };

  const formatDateTime = (date) => {
    const options = {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false
    };
    const jsDate = new Date(date);
    const day = jsDate.getDate().toString().padStart(2, '0');
    const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
    const year = jsDate.getFullYear();
    const hours = jsDate.getHours().toString().padStart(2, '0');
    const minutes = jsDate.getMinutes().toString().padStart(2, '0');
    const seconds = jsDate.getSeconds().toString().padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  };

  return (
    <MainLayout>
      <div>
        <h3 className='text-lg mt-1 mx-8 border-b border-b-gray-300 text-white'>MyWallet</h3>
      </div>
      <div className="mx-auto max-w-[760px]">
        <div className="list-container border border-gray-300 p-4 mt-4">
          {loading ? (
            <Loader />
          ) : (
            <div className='w-full'>
              <div className="flex justify-between items-center border-b border-b-gray-300">
                <h3 className='text-2xl mt-1 mx-2 text-white'><FontAwesomeIcon icon={faGift} className="text-red-500 text-4xl mr-2" />Transactions</h3>
                <button
                  className='bg-purple-700'
                  onClick={() => handleTopupClick(userId)}
                  style={{ maxWidth: '150px' }}
                >
                  Topup / Recharge
                </button>
              </div>
              <div>
                <h3 className='text-lg mt-1 mx-0 bg-green-600 text-white'>Balance Amount:{'    '}
                  {walletBalanceAmount.toFixed(2)}{','}<span className='text-yellow-500'>{'  Balance Points: '}{walletBalancePoints}</span>
                </h3>
              </div>
              <div>
                {walletData && walletData.length === 0 ? (
                  <div className='flex items-center justify-center text-yellow-300'>
                    No records found!
                  </div>
                ) : (
                  <>
                    {sortedWalletData.map((walletItem, index) => (
                      <div key={walletItem._id} className='bg-white my-1 p-4 text-gray-600 rounded-md shadow-md'>
                        <div className='flex'>
                          <span className='font-medium text-black'>
                            <>
                              {index + 1}. {formatDateTime(walletItem.dateTransaction)} {' '} {walletItem.currency} {'  '}
                              {walletItem.inOut !== 1 ? (
                                <span className="text-red-500">(-{walletItem.amount.toFixed(2)})</span>
                              ) : (
                                <><span className="text-blue-500">{walletItem.amount.toFixed(2)}</span></>
                              )}
                            </>
                          </span>
                          <Tooltip text={"View details"} position={"top"}>
                            <span className='ml-auto mr-4 text-blue-500 cursor-pointer' onClick={() => handleWalletView(walletItem._id)}>
                              <i className="fa-solid fa-eye"></i>
                            </span>
                          </Tooltip>
                        </div>
                        {selectedWalletId === walletItem._id && (
                          <div className='bg-gray-200 text-black'>
                            <p><strong>Date:</strong> {formatDateTime(walletItem.dateTransaction)}</p>
                            <p><strong>Particulars:</strong>{walletItem.particulars}</p>
                            <p><strong>Amount:</strong>{walletItem.currency} {walletItem.amount.toFixed(2)} {walletItem.inout}</p>
                            <p><strong>Remarks:</strong> {walletItem.remarks}</p>
                            <p><strong>Mode:</strong> {walletItem.modePayment}</p>
                            <p><strong>Transaction ID:</strong> {walletItem.transactionId}</p>
                            <p><strong>Payment Details:</strong> {walletItem.paymentDetails}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default MyWalletPoints;




